import React, { useEffect } from 'react';
import { Box, Typography, Grid, Paper, Table, TableHead, TableBody, TableRow, TableCell,  TableContainer, } from '@mui/material';
import { getSemenFreezingReport, SemenFreezing } from './SemenFreezingReport.helper';
import { convertUnderscoreToTitle } from '../../../utils/commonUtils';
import { useLoader } from '../../loaderProvider/loader';


interface SemenFreezingReportProps {
  cycle: string;
}

const SemenFreezingReport: React.FC<SemenFreezingReportProps> = ({ cycle }) => {
  const [semenFreezingDetails, setSemenFreezingDetails] = React.useState<SemenFreezing[]>([]);
  const { showLoader, hideLoader } = useLoader();

  useEffect(()=>{
    try {
      showLoader();
      getSemenFreezingReport(cycle).then((res:SemenFreezing[])=>{
        setSemenFreezingDetails(res);
      });
    } catch (error) {
      // eslint-disable-next-line no-console
      console.error('Error fetching semen freezing report:', error);
    } finally {
      hideLoader();
    }
  },[cycle, showLoader, hideLoader]);

  function formatTo12Hour(timeStr: string): string {
    if (!timeStr) {
      return '-';
    }
    const [hourStr, minute] = timeStr.split(':');
    let hour = parseInt(hourStr, 10);

    const period = hour >= 12 ? 'PM' : 'AM';
    hour = hour % 12 || 12; // Convert "0" to "12" for 12 AM

    return `${hour}:${minute} ${period}`;
  }

  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const getDisplayValue = (value: any) => {
    return value !== null && value !== undefined && value !== '' ? value : '-';
  };

  return (
    <Box >
      <Box display="flex" flexDirection="column" gap={2}>
        {/* Box 1: Details */}
        <Box
          p={2}
          width="100%"
        >
          <Typography variant="h6" fontWeight={600} mb={2}>
        Details
          </Typography>
          <Grid container spacing={2}>
            <Grid item xs={12} md={3}>
              <Typography color="text.secondary" fontSize={14}>
              Sample No
              </Typography>
              <Typography fontWeight={600}>{semenFreezingDetails[0]?.sample_number}</Typography>
            </Grid>
            <Grid item xs={12} md={3}>
              <Typography color="text.secondary" fontSize={14}>
              Date
              </Typography>
              <Typography fontWeight={600}>{semenFreezingDetails[0]?.date || '-'}</Typography>
            </Grid>
            <Grid item xs={12} md={3}>
              <Typography color="text.secondary" fontSize={14}>
              Time
              </Typography>
              <Typography fontWeight={600}>{formatTo12Hour(semenFreezingDetails[0]?.time)}</Typography>
            </Grid>
          </Grid>
        </Box>
        <hr/>

        <Box
          p={2}
          width="100%"
        >
          <Typography variant="h6" fontWeight={600} mb={2}>
        Other Details
          </Typography>
          <Grid container spacing={2}>
            <Grid item xs={12} sm={6} md={3}>
              <Typography color="text.secondary" fontSize={14}>
      Sperm Collection Method
              </Typography>
              <Typography fontWeight={600}>
                {getDisplayValue(convertUnderscoreToTitle(semenFreezingDetails[0]?.semen_collection_method))}
              </Typography>
            </Grid>

            {semenFreezingDetails[0]?.semen_collection_method === 'SURGICAL_EXTRACTION' && (
              <Grid item xs={12} sm={6} md={3}>
                <Typography color="text.secondary" fontSize={14}>
        Surgical Extraction Type
                </Typography>
                <Typography fontWeight={600}>
                  {getDisplayValue(semenFreezingDetails[0]?.surgical_extraction_type)}
                </Typography>
              </Grid>
            )}

            <Grid item xs={12} sm={6} md={3}>
              <Typography color="text.secondary" fontSize={14}>
      Collection Date & Time
              </Typography>
              <Typography fontWeight={600}>
                {getDisplayValue(semenFreezingDetails[0]?.collection_date)} at {semenFreezingDetails[0]?.collection_time ? formatTo12Hour(semenFreezingDetails[0]?.collection_time) : '-'}
              </Typography>
            </Grid>

            <Grid item xs={12} sm={6} md={3}>
              <Typography color="text.secondary" fontSize={14}>
      Volume
              </Typography>
              <Typography fontWeight={600}>
                {getDisplayValue(semenFreezingDetails[0]?.volume_ml)}{semenFreezingDetails[0]?.volume_ml ? ' ml' : ''}
              </Typography>
            </Grid>

            <Grid item xs={12} sm={6} md={3}>
              <Typography color="text.secondary" fontSize={14}>
      Sperm Concentration
              </Typography>
              <Typography fontWeight={600}>
                {getDisplayValue(semenFreezingDetails[0]?.sperm_concentration_million_per_ml)}{semenFreezingDetails[0]?.sperm_concentration_million_per_ml ? ' m/mL' : ''}
              </Typography>
            </Grid>
          </Grid>

        </Box>
      </Box>
      <hr/>

      <Box mt={4}>
        <Typography variant="h6" fontWeight={600} mb={2}>
        Cryostorage Details
        </Typography>

        <TableContainer component={Paper} sx={{ borderRadius: 2, boxShadow: 'none', border: '1px solid #ddd' }}>
          <Table sx={{ borderCollapse: 'collapse' }}>
            <TableHead sx={{ backgroundColor: '#f9f9f9' }}>
              <TableRow>
                {[
                  'Cryo No.',
                  'Tank',
                  'Canister',
                  'Cane',
                  'Vials',
                  'Volume',
                  'Expiry Date',
                  'Remark',
                ].map((header, idx) => (
                  <TableCell key={idx} sx={{ border: '1px solid #ddd', fontWeight: 'bold' }}>
                    {header}
                  </TableCell>
                ))}
              </TableRow>
            </TableHead>

            <TableBody>
              {semenFreezingDetails[0]?.cryo.map((row, index) => (
                <TableRow key={index}>
                  <TableCell sx={{ border: '1px solid #ddd', fontWeight: 600 }}>{row.id}</TableCell>
                  <TableCell sx={{ border: '1px solid #ddd' }}>{row.location_details.parents[0].name}</TableCell>
                  <TableCell sx={{ border: '1px solid #ddd' }}>{row.location_details.parents[1].name}</TableCell>
                  <TableCell sx={{ border: '1px solid #ddd' }}>{row.location_details.parents[2].name}</TableCell>
                  <TableCell sx={{ border: '1px solid #ddd' }}>{row.location_details.name}</TableCell>
                  <TableCell sx={{ border: '1px solid #ddd' }}>{row.semen_volume}</TableCell>
                  <TableCell sx={{ border: '1px solid #ddd' }}>{row.expiry_date}</TableCell>
                  <TableCell sx={{ border: '1px solid #ddd' }}>{row.remark}</TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>

      </Box>

      <Box
        display="flex"
        flexWrap="wrap"
        gap={10}
        mt={2}
        p={2}
      >
        <Grid container spacing={2}>
          <Grid item xs={12} md={3}>
            <Typography color="text.secondary" fontSize={14}>
              No. of Vials
            </Typography>
            <Typography fontWeight={600}>2</Typography>
          </Grid>
          <Grid item xs={12} md={3}>
            <Typography color="text.secondary" fontSize={14}>
              Done By
            </Typography>
            <Typography fontWeight={600}>{semenFreezingDetails[0]?.andrologist_name}</Typography>
          </Grid>
          <Grid item xs={12} md={3}>
            <Typography color="text.secondary" fontSize={14}>
              Witnessed By
            </Typography>
            <Typography fontWeight={600}>{semenFreezingDetails[0]?.witnessed_by_name}</Typography>
          </Grid>
        </Grid>
      </Box>


    </Box>
  );
};

export default SemenFreezingReport;
